import { supabase, SUPABASE_URL } from "@/integrations/supabase/client";
import type {
  BookChatRequest,
  BookChatResponse,
  AskAiRequest,
  AskAiResponse,
  ChatSession,
  ChatMessage,
  ChatSessionFilters,
  FeedbackAnalytics,
} from "@/types/chat";

/**
 * Send a chat message via the book-chat edge function.
 */
export async function sendChatMessage(
  request: BookChatRequest
): Promise<BookChatResponse> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error("Not authenticated");
  }

  const response = await fetch(`${SUPABASE_URL}/functions/v1/book-chat`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to send message");
  }

  return response.json();
}

/**
 * Send a chat message via the ask-ai edge function.
 */
export async function sendAskAiMessage(
  request: AskAiRequest
): Promise<AskAiResponse> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error("Not authenticated");
  }

  const response = await fetch(`${SUPABASE_URL}/functions/v1/ask-ai`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to send message");
  }

  return response.json();
}

/**
 * Send a streaming chat message via the book-chat edge function (SSE).
 */
export async function sendChatMessageStream(
  request: BookChatRequest,
  onToken: (text: string) => void,
  onDone: (meta: BookChatResponse) => void,
  onError: (error: string) => void,
): Promise<void> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error("Not authenticated");
  }

  const response = await fetch(`${SUPABASE_URL}/functions/v1/book-chat`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ ...request, stream: true }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to send message");
  }

  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() || "";

    for (const line of lines) {
      if (!line.startsWith("data: ")) continue;
      const jsonStr = line.slice(6).trim();
      if (!jsonStr) continue;

      try {
        const event = JSON.parse(jsonStr);
        switch (event.type) {
          case "token":
            onToken(event.content);
            break;
          case "done":
            onDone({
              answer: "", // Full text was streamed via tokens
              sources: event.sources || [],
              sessionId: event.sessionId,
              messageId: event.messageId,
              credits: event.credits || undefined,
            });
            break;
          case "error":
            onError(event.message || "Stream error");
            break;
        }
      } catch {
        // Skip malformed SSE lines
      }
    }
  }
}

/**
 * Send a streaming chat message via the ask-ai edge function (SSE).
 */
export async function sendAskAiMessageStream(
  request: AskAiRequest,
  onToken: (text: string) => void,
  onDone: (meta: AskAiResponse) => void,
  onError: (error: string) => void,
): Promise<void> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error("Not authenticated");
  }

  const response = await fetch(`${SUPABASE_URL}/functions/v1/ask-ai`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ ...request, stream: true }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to send message");
  }

  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() || "";

    for (const line of lines) {
      if (!line.startsWith("data: ")) continue;
      const jsonStr = line.slice(6).trim();
      if (!jsonStr) continue;

      try {
        const event = JSON.parse(jsonStr);
        switch (event.type) {
          case "token":
            onToken(event.content);
            break;
          case "done":
            onDone({
              answer: "", // Full text was streamed via tokens
              sources: event.sources || [],
              recommendedBooks: event.recommendedBooks,
              sessionId: event.sessionId,
              messageId: event.messageId,
              credits: event.credits || undefined,
              persona: event.persona,
            });
            break;
          case "error":
            onError(event.message || "Stream error");
            break;
        }
      } catch {
        // Skip malformed SSE lines
      }
    }
  }
}

/**
 * Fetch chat sessions for admin list with pagination, search, and filtering.
 */
export async function fetchChatSessions(
  filters: ChatSessionFilters
): Promise<{ data: ChatSession[]; count: number }> {
  const { page, pageSize, search, bookId, chatType } = filters;
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  let query = supabase
    .from("chat_sessions")
    .select(
      `id, user_id, book_id, title, language, message_count, is_active, chat_type, persona, positive_feedback_count, negative_feedback_count, created_at, updated_at,
       books(title)`,
      { count: "exact" }
    )
    .order("updated_at", { ascending: false })
    .range(from, to);

  if (search) {
    query = query.ilike("title", `%${search}%`);
  }

  if (bookId) {
    query = query.eq("book_id", bookId);
  }

  if (chatType) {
    query = query.eq("chat_type", chatType);
  }

  const { data, count, error } = await query;

  if (error) throw error;

  // Fetch profile info + subscription status for the user_ids
  const userIds = [...new Set((data || []).map((r: any) => r.user_id))];
  let profileMap: Record<string, { name: string; email: string; subscription_status?: string }> = {};
  if (userIds.length > 0) {
    const { data: profiles } = await supabase
      .from("profiles")
      .select("uid, id, name, email")
      .in("uid", userIds);
    if (profiles) {
      // Get subscription status for these profile IDs
      const profileIds = profiles.map((p: any) => p.id);
      const { data: subs } = await supabase
        .from("user_subscriptions")
        .select("user_id, status, subscription_end")
        .in("user_id", profileIds)
        .order("created_at", { ascending: false });

      // Build a map of profile_id -> subscription status
      // Check both status field AND subscription_end date (status may not always be updated)
      const subMap: Record<number, string> = {};
      if (subs) {
        for (const s of subs) {
          if (!subMap[s.user_id]) {
            const endDate = s.subscription_end ? new Date(s.subscription_end) : null;
            const isExpired = endDate && endDate < new Date();

            if (s.status === "cancelled") {
              subMap[s.user_id] = "cancelled";
            } else if (isExpired) {
              subMap[s.user_id] = "expired";
            } else if (s.status === "active" && !isExpired) {
              subMap[s.user_id] = "active";
            } else {
              subMap[s.user_id] = s.status;
            }
          }
        }
      }

      for (const p of profiles) {
        profileMap[p.uid] = {
          name: p.name,
          email: p.email,
          subscription_status: subMap[p.id] || "free",
        };
      }
    }
  }

  // Transform joined data
  const sessions: ChatSession[] = (data || []).map((row: any) => {
    const profile = profileMap[row.user_id];
    return {
      id: row.id,
      user_id: row.user_id,
      book_id: row.book_id,
      title: row.title,
      language: row.language,
      context_summary: null,
      summarized_up_to: 0,
      message_count: row.message_count,
      total_user_tokens: 0,
      total_assistant_tokens: 0,
      is_active: row.is_active,
      chat_type: row.chat_type || "book",
      persona: row.persona || "book_assistant",
      positive_feedback_count: row.positive_feedback_count || 0,
      negative_feedback_count: row.negative_feedback_count || 0,
      created_at: row.created_at,
      updated_at: row.updated_at,
      book_title: row.books?.title,
      user_name: profile?.name,
      user_email: profile?.email,
      subscription_status: profile?.subscription_status || "free",
    };
  });

  return { data: sessions, count: count || 0 };
}

/**
 * Fetch a single chat session by ID (admin view).
 */
export async function fetchChatSession(
  sessionId: number
): Promise<ChatSession> {
  const { data, error } = await supabase
    .from("chat_sessions")
    .select(`*, books(title)`)
    .eq("id", sessionId)
    .single();

  if (error) throw error;

  // Fetch profile info + subscription status
  let userName: string | undefined;
  let userEmail: string | undefined;
  let subscriptionStatus = "free";
  if (data.user_id) {
    const { data: profile } = await supabase
      .from("profiles")
      .select("id, name, email")
      .eq("uid", data.user_id)
      .single();
    userName = profile?.name;
    userEmail = profile?.email;

    if (profile?.id) {
      const { data: sub } = await supabase
        .from("user_subscriptions")
        .select("status, subscription_end")
        .eq("user_id", profile.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (sub) {
        const endDate = sub.subscription_end ? new Date(sub.subscription_end) : null;
        const isExpired = endDate && endDate < new Date();
        if (sub.status === "cancelled") subscriptionStatus = "cancelled";
        else if (isExpired) subscriptionStatus = "expired";
        else if (sub.status === "active") subscriptionStatus = "active";
        else subscriptionStatus = sub.status;
      }
    }
  }

  return {
    ...data,
    book_title: (data as any).books?.title,
    user_name: userName,
    user_email: userEmail,
    subscription_status: subscriptionStatus,
  } as ChatSession;
}

/**
 * Fetch chat sessions for the current user (optionally filtered by book).
 */
export async function fetchUserChatSessions(
  bookId?: number,
  chatType?: string
): Promise<ChatSession[]> {
  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) throw new Error("Not authenticated");

  let query = supabase
    .from("chat_sessions")
    .select("*")
    .eq("user_id", user.id)
    .order("updated_at", { ascending: false });

  if (bookId) {
    query = query.eq("book_id", bookId);
  }

  if (chatType) {
    query = query.eq("chat_type", chatType);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data || []).map((row: any) => ({
    ...row,
    chat_type: row.chat_type || "book",
    persona: row.persona || "book_assistant",
  })) as ChatSession[];
}

/**
 * Fetch messages for a session.
 */
export async function fetchChatMessages(
  sessionId: number,
  limit = 100,
  offset = 0
): Promise<ChatMessage[]> {
  const { data, error } = await supabase
    .from("chat_messages")
    .select("*")
    .eq("session_id", sessionId)
    .order("created_at", { ascending: true })
    .range(offset, offset + limit - 1);

  if (error) throw error;
  return data as ChatMessage[];
}

/**
 * Delete a chat session.
 */
export async function deleteChatSession(sessionId: number): Promise<void> {
  const { error } = await supabase
    .from("chat_sessions")
    .delete()
    .eq("id", sessionId);

  if (error) throw error;
}

/**
 * Fetch total stats for admin dashboard cards.
 */
export async function fetchChatStats(): Promise<{
  totalSessions: number;
  totalMessages: number;
}> {
  const [sessionsResult, messagesResult] = await Promise.all([
    supabase
      .from("chat_sessions")
      .select("id", { count: "exact", head: true }),
    supabase
      .from("chat_messages")
      .select("id", { count: "exact", head: true }),
  ]);

  return {
    totalSessions: sessionsResult.count || 0,
    totalMessages: messagesResult.count || 0,
  };
}

/**
 * Submit feedback (thumbs up/down) on an assistant message.
 */
export async function submitMessageFeedback(
  messageId: number,
  feedback: 1 | -1 | null,
  comment?: string
): Promise<void> {
  const { error } = await supabase.rpc("submit_message_feedback", {
    p_message_id: messageId,
    p_feedback: feedback,
    p_comment: comment || null,
  });
  if (error) throw error;
}

/**
 * Fetch feedback analytics for admin dashboard.
 */
export async function fetchFeedbackAnalytics(
  hours = 168
): Promise<FeedbackAnalytics> {
  const { data, error } = await supabase.rpc("fetch_feedback_analytics", {
    p_hours: hours,
  });
  if (error) throw error;
  return data as FeedbackAnalytics;
}
